/**
 * Quotation create + edit page — production-grade builder.
 *
 * Single-page vertical layout (no right-rail) that mirrors the
 * "create-quotation" mockup the user signed off on:
 *
 *   ┌─ Company banner (org letterhead snapshot)
 *   ├─ §1  Bill to (client)
 *   ├─ §2  Quotation details (number / date / valid / reference)
 *   ├─ §3  Modules & line items
 *   │       ─ Drag-to-reorder modules + items
 *   │       ─ Per-item discount %
 *   │       ─ "Add from Library" pulls from useMaterialLibrary
 *   │       ─ Inline add-row form at the bottom of each module
 *   ├─ §4  Tax & discount  + live totals preview
 *   ├─ §5  Payment milestones with sum-vs-100% status pill
 *   └─ §6  Notes & T&C
 *   ┌─ Sticky action bar (modules · items · total · Reset · Preview)
 *
 * Preview is an inline mode toggle (not a modal) — clicking Preview
 * swaps the builder for a printable layout in the same page. Print
 * uses the same `buildQuotationHtml` so screen + PDF stay identical.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

import { canCRMWrite } from '@shared/perms';

import { useAuth } from '@/auth/AuthProvider';
import { PagePlaceholder } from '@/components/PagePlaceholder';
import { NewProjectModal as _RefIgnore } from '@/components/NewProjectModal';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
void _RefIgnore;
import {
  computeQuotationTotals,
  createQuotation,
  DEFAULT_MILESTONES,
  DEFAULT_TNC,
  distributeMilestones,
  formatInr,
  DIMENSION_UNITS,
  GST_RATES,
  deriveQuantityFromDimensions,
  deriveRateFromCostAndMarkup,
  localId,
  nextQuotationNumber,
  QUOTATION_UNITS,
  saveQuotationRevision,
  updateQuotation,
  type DimensionUnit,
  type DiscountType,
  type GstConfig,
  type GstRate,
  type Quotation,
  type QuotationItem,
  type QuotationSection,
  type QuotationStatus,
} from '@/lib/quotations';
import { buildQuotationHtml } from '@/lib/quotationReport';
import { openHtmlForPrint } from '@/lib/projectReports';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { ServicePickerModal } from '@/components/ServicePickerModal';
import {
  type ServiceLibraryItem,
} from '@/lib/serviceLibrary';
import { useParties } from '@/lib/useParties';
import {
  useQuotation,
  useQuotationRevisions,
} from '@/lib/useQuotations';
// Materials library no longer imported here — the quote form's
// "From Library" picker pulls from the Services library now
// (this is a SERVICES quotation app, not a materials trader).

// ───── Inline-style helpers (kept in-file to match the design exactly
//       without polluting iio.css with one-off rules) ──────────────

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid var(--border)',
  borderRadius: 10,
  background: '#fff',
  fontSize: 14,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const FIELD_LABEL: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 6,
  display: 'block',
};

// ───── Date helpers ────────────────────────────────────────────

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function plusDaysKey(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function tsFromDateKey(key: string): Timestamp | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  return Timestamp.fromDate(new Date(y, m - 1, d, 12, 0, 0, 0));
}
function dateKeyFromTs(
  ts: { toMillis(): number } | null | undefined,
): string {
  if (!ts) return '';
  const d = new Date(ts.toMillis());
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ───── Defaults ────────────────────────────────────────────────

function emptyItem(): QuotationItem {
  return {
    id: localId('it'),
    name: '',
    description: '',
    unit: 'Sq.ft',
    quantity: 1,
    rate: 0,
    discount: 0,
    amount: 0,
  };
}
function emptySection(title = 'New module'): QuotationSection {
  return { id: localId('sec'), title, items: [] };
}

// ───── Module display helpers ──────────────────────────────────

/** Per-item amount with item-discount applied. */
function itemAmount(it: QuotationItem): number {
  const disc = Math.max(0, Math.min(100, it.discount ?? 0));
  return Math.round(it.quantity * it.rate * (1 - disc / 100) * 100) / 100;
}
function moduleSubtotal(s: QuotationSection): number {
  return s.items.reduce((sum, it) => sum + itemAmount(it), 0);
}

// ───── Component ───────────────────────────────────────────────

export function CreateQuotationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userDoc } = useCurrentUser();
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canCRMWrite(orgRole);
  const orgData = (org?.data ?? {}) as {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstin?: string;
    logoUrl?: string;
    signatureUrl?: string;
    bankAccountName?: string;
    bankName?: string;
    bankBranch?: string;
    bankAccountNumber?: string;
    bankIfsc?: string;
    bankUpiId?: string;
  };

  const { quotationId } = useParams<{ quotationId?: string }>();
  const isEdit = !!quotationId;
  const { quotation: existing, loading: loadingExisting } = useQuotation(
    quotationId ?? null,
  );
  // Revision history is read on every render of the wizard — cheap
  // (a single live subscription) and lets Step 4 surface the
  // version list inline so the user doesn't have to bounce to the
  // detail page to see history.
  const { revisions } = useQuotationRevisions(quotationId ?? null);
  // When the user clicks a past revision row, we render THAT
  // snapshot in the preview iframe instead of the live form. Null
  // = show the in-progress form state (default).
  const [viewingRevisionVersion, setViewingRevisionVersion] = useState<
    number | null
  >(null);

  const { parties } = useParties();
  // Services library is loaded on-demand inside the picker
  // modal — no need to subscribe at the page level.

  // ── Form state ────────────────────────────────────────────────
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(todayKey());
  const [validUntil, setValidUntil] = useState(plusDaysKey(15));
  const [reference, setReference] = useState('');

  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientGstin, setClientGstin] = useState('');

  const [sections, setSections] = useState<QuotationSection[]>(() => [
    emptySection('Design Phase'),
  ]);

  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [discountValue, setDiscountValue] = useState(0);
  const [gst, setGst] = useState<GstConfig>({
    enabled: true,
    type: 'intra',
    rate: 18,
  });

  const [milestonePercents, setMilestonePercents] = useState(
    DEFAULT_MILESTONES.map((m) => ({
      id: localId('ms'),
      label: m.label,
      percent: m.percent,
    })),
  );

  const [tncText, setTncText] = useState(DEFAULT_TNC.join('\n'));
  const [notes, setNotes] = useState('');
  const [showBank, setShowBank] = useState(true);
  const [showSignature, setShowSignature] = useState(true);

  const [revisionNote, setRevisionNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Wizard step ──────────────────────────────────────────────
  // 1 Customer · 2 Items · 3 Billing · 4 Review.
  // We don't enforce strict linear flow — the user can click any
  // earlier step to jump back. Forward jumps still validate the
  // current step (handled in `goNext()`).
  type StepKey = 1 | 2 | 3 | 4;
  const STEPS: { key: StepKey; label: string; icon: string }[] = [
    { key: 1, label: 'Customer', icon: 'ph ph-user' },
    { key: 2, label: 'Items',    icon: 'ph ph-package' },
    { key: 3, label: 'Billing',  icon: 'ph ph-calculator' },
    { key: 4, label: 'Review',   icon: 'ph ph-eye' },
  ];
  const [step, setStep] = useState<StepKey>(1);

  // ── Hydrate from existing doc when editing ────────────────────
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isEdit) return;
    if (hydratedRef.current) return;
    if (loadingExisting) return;
    if (!existing) return;
    hydratedRef.current = true;
    setNumber(existing.number);
    setDate(dateKeyFromTs(existing.date) || todayKey());
    setValidUntil(dateKeyFromTs(existing.validUntil) || plusDaysKey(15));
    setReference(existing.projectName ?? '');
    setClientId(existing.clientId);
    setClientName(existing.clientName);
    setClientPhone(existing.clientPhone ?? '');
    setClientEmail(existing.clientEmail ?? '');
    setClientAddress(existing.clientAddress ?? '');
    setClientGstin(existing.clientGstin ?? '');
    setSections(
      existing.sections.length > 0 ? existing.sections : [emptySection()],
    );
    setDiscountType(existing.discountType);
    setDiscountValue(existing.discountValue);
    setGst(existing.gst);
    setMilestonePercents(
      existing.paymentMilestones.map((m) => ({
        id: m.id,
        label: m.label,
        percent: m.percent,
      })),
    );
    setTncText(existing.termsAndConditions.join('\n'));
    setNotes(existing.notes ?? '');
    setShowBank(existing.showBankDetails);
    setShowSignature(existing.showSignature);
  }, [isEdit, loadingExisting, existing]);

  // ── Auto-number on create ─────────────────────────────────────
  useEffect(() => {
    if (isEdit) return;
    if (number) return;
    if (!orgId) return;
    let cancelled = false;
    nextQuotationNumber(orgId)
      .then((n) => {
        if (!cancelled) setNumber(n);
      })
      .catch((e) => console.warn('[CreateQuotation] number gen failed:', e));
    return () => {
      cancelled = true;
    };
  }, [isEdit, number, orgId]);

  // ── Live totals ──────────────────────────────────────────────
  const totals = useMemo(() => {
    const cleanedSections = sections.map((s) => ({
      ...s,
      items: s.items.map((it) => ({ ...it, amount: itemAmount(it) })),
    }));
    return {
      ...computeQuotationTotals({
        sections: cleanedSections,
        discountType,
        discountValue,
        gst,
      }),
      sections: cleanedSections,
    };
  }, [sections, discountType, discountValue, gst]);

  const milestoneRows = useMemo(
    () => distributeMilestones(milestonePercents, totals.total),
    [milestonePercents, totals.total],
  );
  const milestoneSum = milestonePercents.reduce(
    (s, m) => s + (Number.isFinite(m.percent) ? m.percent : 0),
    0,
  );
  const milestoneSumOk = Math.abs(milestoneSum - 100) < 0.01;
  const itemCount = sections.reduce((c, s) => c + s.items.length, 0);

  // Margin aggregates — internal-only. Walks every item across
  // every module and sums Cost (costPrice × qty) + Sale
  // (rate × qty, ignoring per-line discount because the studio
  // wants to see PRE-discount margin). Only items with a
  // costPrice contribute to Cost; items without are excluded
  // so the margin display reflects what the studio chose to
  // track. Used by the sticky action bar to surface margin
  // in real time. NEVER printed on the client PDF.
  const marginTotals = useMemo(() => {
    let costSum = 0;
    let saleSum = 0;
    let trackedCount = 0;
    for (const s of sections) {
      for (const it of s.items) {
        const qty = it.quantity ?? 0;
        const cp = it.costPrice ?? 0;
        if (cp > 0) {
          costSum += cp * qty;
          saleSum += (it.rate ?? 0) * qty;
          trackedCount += 1;
        }
      }
    }
    return {
      cost: costSum,
      sale: saleSum,
      markup: saleSum - costSum,
      trackedCount,
    };
  }, [sections]);

  // ── Module / item mutators ────────────────────────────────────
  function updateSection(id: string, patch: Partial<QuotationSection>) {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }
  function deleteSection(id: string) {
    if (sections.length <= 1) {
      // Keep at least one module shell — clearing instead of deleting.
      setSections([emptySection('New module')]);
      return;
    }
    setSections((prev) => prev.filter((s) => s.id !== id));
  }
  function addSection() {
    setSections((prev) => [
      ...prev,
      emptySection(`Module ${prev.length + 1}`),
    ]);
  }
  function appendItem(secId: string, base: Partial<QuotationItem> = {}) {
    setSections((prev) =>
      prev.map((s) =>
        s.id === secId
          ? {
              ...s,
              items: [
                ...s.items,
                {
                  ...emptyItem(),
                  ...base,
                  id: localId('it'),
                  amount: itemAmount({ ...emptyItem(), ...base } as QuotationItem),
                },
              ],
            }
          : s,
      ),
    );
  }
  function updateItem(
    secId: string,
    itemId: string,
    patch: Partial<QuotationItem>,
  ) {
    setSections((prev) =>
      prev.map((s) =>
        s.id === secId
          ? {
              ...s,
              items: s.items.map((it) => {
                if (it.id !== itemId) return it;
                const merged = { ...it, ...patch };
                return { ...merged, amount: itemAmount(merged) };
              }),
            }
          : s,
      ),
    );
  }
  function deleteItem(secId: string, itemId: string) {
    setSections((prev) =>
      prev.map((s) =>
        s.id === secId
          ? { ...s, items: s.items.filter((it) => it.id !== itemId) }
          : s,
      ),
    );
  }

  // ── Drag-to-reorder (HTML5 native, same pattern as Kanban) ───
  const dragModuleRef = useRef<string | null>(null);
  const dragItemRef = useRef<{ secId: string; itemId: string } | null>(null);

  function reorderModule(targetId: string) {
    const src = dragModuleRef.current;
    if (!src || src === targetId) return;
    setSections((prev) => {
      const next = [...prev];
      const srcIdx = next.findIndex((s) => s.id === src);
      const tgtIdx = next.findIndex((s) => s.id === targetId);
      if (srcIdx < 0 || tgtIdx < 0) return prev;
      const [moved] = next.splice(srcIdx, 1);
      next.splice(tgtIdx, 0, moved);
      return next;
    });
  }
  function reorderItem(secId: string, targetItemId: string) {
    const src = dragItemRef.current;
    if (!src || src.secId !== secId || src.itemId === targetItemId) return;
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== secId) return s;
        const next = [...s.items];
        const srcIdx = next.findIndex((it) => it.id === src.itemId);
        const tgtIdx = next.findIndex((it) => it.id === targetItemId);
        if (srcIdx < 0 || tgtIdx < 0) return s;
        const [moved] = next.splice(srcIdx, 1);
        next.splice(tgtIdx, 0, moved);
        return { ...s, items: next };
      }),
    );
  }

  // ── Milestone mutators ────────────────────────────────────────
  function addMilestone() {
    setMilestonePercents((prev) => [
      ...prev,
      { id: localId('ms'), label: 'New milestone', percent: 0 },
    ]);
  }
  function updateMilestone(
    idx: number,
    patch: Partial<{ label: string; percent: number }>,
  ) {
    setMilestonePercents((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
    );
  }
  function deleteMilestone(idx: number) {
    setMilestonePercents((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Save ──────────────────────────────────────────────────────
  async function handleSave(target: QuotationStatus) {
    setError(null);
    if (!orgId || !user) return setError('No organisation context.');
    if (!clientName.trim()) return setError('Client name is required.');
    if (itemCount === 0)
      return setError('Add at least one line item before saving.');

    setBusy(true);
    try {
      const createdByName =
        ((userDoc?.data?.displayName as string | undefined) ?? '').trim() ||
        user.email ||
        'You';

      const cleanedTnC = tncText
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);

      const docPatch = {
        number,
        status: target,
        date: tsFromDateKey(date),
        validUntil: tsFromDateKey(validUntil),
        clientId,
        clientName: clientName.trim(),
        clientPhone: clientPhone.trim() || undefined,
        clientEmail: clientEmail.trim() || undefined,
        clientAddress: clientAddress.trim() || undefined,
        clientGstin: clientGstin.trim() || undefined,
        // Workflow: quote → project, not the other way around.
        // `reference` is a free-text reference (e.g. "Project Atlas
        // — Phase 1") shown on the printed PDF; it doesn't link to a
        // Firestore project doc. If editing an existing quote that's
        // already linked to a project, preserve the link.
        projectId: existing?.projectId ?? null,
        projectName: reference.trim() || existing?.projectName || undefined,
        sections: totals.sections,
        discountType,
        discountValue,
        discountAmount: totals.discountAmount,
        subtotal: totals.subtotal,
        taxableAmount: totals.taxableAmount,
        gst,
        cgstAmount: totals.cgstAmount,
        sgstAmount: totals.sgstAmount,
        igstAmount: totals.igstAmount,
        taxAmount: totals.taxAmount,
        roundOff: totals.roundOff,
        total: totals.total,
        paymentMilestones: milestoneRows,
        termsAndConditions: cleanedTnC,
        notes: notes.trim() || undefined,
        companyName: orgData.name ?? 'Studio',
        companyAddress: orgData.address ?? undefined,
        companyPhone: orgData.phone ?? undefined,
        companyEmail: orgData.email ?? undefined,
        companyGstin: orgData.gstin ?? undefined,
        companyLogoUrl: orgData.logoUrl ?? undefined,
        companySignatureUrl: orgData.signatureUrl ?? undefined,
        showBankDetails: showBank,
        showSignature,
        bankDetails: showBank ? buildBankDetails(orgData) : undefined,
      };

      if (isEdit && quotationId && existing) {
        if (existing.status === 'draft') {
          await updateQuotation(quotationId, docPatch);
        } else {
          await saveQuotationRevision({
            id: quotationId,
            current: existing,
            note: revisionNote || 'Quote updated',
            patch: docPatch,
            createdBy: user.uid,
            createdByName,
          });
        }
        navigate(`/quotations/${quotationId}`);
      } else {
        const newId = await createQuotation({
          orgId,
          ...docPatch,
          createdBy: user.uid,
          createdByName,
        });
        navigate(`/quotations/${newId}`);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function handlePrintFromBuilder() {
    if (itemCount === 0) {
      setError('Add at least one line item to preview.');
      return;
    }
    openHtmlForPrint(
      buildQuotationHtml(buildSyntheticQuotation()),
      `${number || 'Quotation'} — ${clientName || 'Preview'}`,
    );
  }

  // ── Step navigation ──────────────────────────────────────────
  function validateStep(s: StepKey): string | null {
    if (s === 1) {
      if (!clientName.trim()) return 'Add the client name before moving on.';
    } else if (s === 2) {
      if (itemCount === 0)
        return 'Add at least one line item before moving on.';
    } else if (s === 3) {
      // Milestone-sum is a soft warning — saving is allowed even
      // when it doesn't add to 100%, since some studios use ad-hoc
      // schedules. So no hard blocker here.
    }
    return null;
  }
  function goToStep(target: StepKey) {
    setError(null);
    if (target > step) {
      // Validate the current step before letting the user skip
      // forward. Backwards jumps + same-step clicks are free.
      const msg = validateStep(step);
      if (msg) {
        setError(msg);
        return;
      }
    }
    setStep(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function goNext() {
    if (step >= 4) return;
    goToStep((step + 1) as StepKey);
  }
  function goPrev() {
    if (step <= 1) return;
    setError(null);
    setStep((s) => (s - 1) as StepKey);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function buildSyntheticQuotation(): Quotation {
    return {
      id: 'preview',
      orgId: orgId ?? '',
      number: number || 'QT-PREVIEW',
      version: existing?.version ?? 0,
      status: 'draft',
      date: tsFromDateKey(date),
      validUntil: tsFromDateKey(validUntil),
      clientId,
      clientName: clientName.trim() || '(client name pending)',
      clientPhone: clientPhone || undefined,
      clientEmail: clientEmail || undefined,
      clientAddress: clientAddress || undefined,
      clientGstin: clientGstin || undefined,
      projectId: existing?.projectId ?? null,
      projectName: reference || undefined,
      sections: totals.sections,
      subtotal: totals.subtotal,
      discountType,
      discountValue,
      discountAmount: totals.discountAmount,
      taxableAmount: totals.taxableAmount,
      gst,
      cgstAmount: totals.cgstAmount,
      sgstAmount: totals.sgstAmount,
      igstAmount: totals.igstAmount,
      taxAmount: totals.taxAmount,
      roundOff: totals.roundOff,
      total: totals.total,
      paymentMilestones: milestoneRows,
      termsAndConditions: tncText
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean),
      notes: notes || undefined,
      companyName: orgData.name ?? 'Studio',
      companyAddress: orgData.address,
      companyPhone: orgData.phone,
      companyEmail: orgData.email,
      companyGstin: orgData.gstin,
      companyLogoUrl: orgData.logoUrl,
      companySignatureUrl: orgData.signatureUrl,
      bankDetails: showBank ? buildBankDetails(orgData) : undefined,
      showBankDetails: showBank,
      showSignature,
      createdAt: null,
      updatedAt: null,
      createdBy: user?.uid ?? '',
    };
  }

  // ───────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────

  // Role gate — non-CRM-write roles see a placeholder. Mirrors
  // CreateLead pattern.
  if (orgRole && !canWrite) {
    return (
      <PagePlaceholder
        title="Access denied"
        body="Only Super Admin, Admin, or Manager can create or edit quotations. Ask one of them to handle this."
      />
    );
  }

  return (
    <div
      className="page active"
      id="page-create-quotation"
      style={{
        display: 'block',
        // Constrain the wizard to a document-width column so the
        // form fields don't stretch edge-to-edge. Matches the
        // A4-style breathing room of the inline preview on Step 4
        // — keeps the eye anchored, reduces line length on long
        // text inputs, and lines up visually with what the
        // generated quotation PDF will look like.
        maxWidth: 980,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
      }}
    >
      <div className="projects-header" style={{ marginBottom: 16 }}>
        <div className="projects-title-row">
          <h1>{isEdit ? 'Edit quotation' : 'New quotation'}</h1>
          <div className="projects-stats">
            {step === 4
              ? 'Review · what the client will see'
              : number
                ? `Reference ${number}${isEdit && existing && existing.version > 0 ? ` · Currently R${existing.version}` : ''}`
                : 'Auto-numbered on save'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              isEdit
                ? navigate(`/quotations/${quotationId}`)
                : navigate('/quotations')
            }
          >
            <i className="ph ph-arrow-left" /> Back
          </button>
        </div>
      </div>

      {/* ── Stepper (progress indicator) ──
          4 evenly-spaced steps with a connector line that fills
          green as the user advances. Active step gets a deeper
          brand badge + dark label; done steps get a green
          checkmark badge + green label. Pending steps stay light
          grey. Clickable to navigate. Each step shows a tiny
          subtitle under the label so the user knows what's on
          each page without committing a click. */}
      <div
        className="card"
        style={{
          padding: '18px 22px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          overflowX: 'auto',
        }}
      >
        {STEPS.map((s, i) => {
          const active = s.key === step;
          const done = s.key < step;
          // Map step keys to a brief subtitle — answers "what
          // do I do here?" before the user even clicks.
          const subtitleByKey: Record<number, string> = {
            1: 'Client + dates',
            2: 'Line items',
            3: 'Tax + bank',
            4: 'Preview + send',
          };
          const subtitle = subtitleByKey[s.key];

          return (
            <div
              key={s.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                flexShrink: 0,
                flex: 1,
                minWidth: 0,
              }}
            >
              <button
                type="button"
                onClick={() => goToStep(s.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '4px 8px',
                  borderRadius: 10,
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'background 0.15s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
                title={`Step ${s.key}: ${s.label}`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#F9FAFB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: active
                      ? 'var(--brand)'
                      : done
                        ? '#15803D'
                        : '#fff',
                    border: active
                      ? '2px solid var(--brand)'
                      : done
                        ? '2px solid #15803D'
                        : '2px solid var(--border)',
                    color: active || done ? '#fff' : 'var(--text-tertiary)',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 13,
                    fontWeight: 800,
                    flexShrink: 0,
                    transition: 'all 0.2s',
                    boxShadow: active
                      ? '0 0 0 4px rgba(220, 38, 38, 0.12)'
                      : 'none',
                  }}
                >
                  {done ? (
                    <i className="ph-bold ph-check" style={{ fontSize: 14 }} />
                  ) : (
                    s.key
                  )}
                </span>
                <div style={{ textAlign: 'left' }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 700 : 600,
                      color: active
                        ? 'var(--text-primary)'
                        : done
                          ? '#15803D'
                          : 'var(--text-secondary)',
                      lineHeight: 1.2,
                    }}
                  >
                    {s.label}
                  </div>
                  {subtitle ? (
                    <div
                      style={{
                        fontSize: 10.5,
                        color: 'var(--text-tertiary)',
                        marginTop: 2,
                        letterSpacing: 0.2,
                      }}
                    >
                      {subtitle}
                    </div>
                  ) : null}
                </div>
              </button>
              {i < STEPS.length - 1 ? (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    background: done ? '#15803D' : 'var(--border)',
                    margin: '0 12px',
                    borderRadius: 2,
                    minWidth: 20,
                    transition: 'background 0.25s',
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {error ? (
        <div
          className="card"
          style={{
            padding: 12,
            background: 'var(--critical-bg)',
            color: 'var(--critical)',
            border: '1px solid #FECACA',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 13,
          }}
        >
          <span>{error}</span>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setError(null)}
            style={{ fontSize: 12 }}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* ============ STEP 1 — Customer ============ */}
      {step === 1 ? (
        <div
          className="card"
          style={{ padding: 0, overflow: 'hidden', marginBottom: 90 }}
        >
          {/* Company banner — shown on step 1 so the user sees the
              letterhead context while filling in the client. */}
          <CompanyBanner org={orgData} />

          {/* §1 Bill to (client) */}
          <Section
            num={1}
            title="Bill to (client)"
            subtitle="Who is this quotation for?"
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr 1fr',
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <label style={FIELD_LABEL}>Pick from contacts</label>
                <select
                  style={INPUT}
                  value={clientId ?? (clientName ? '__manual__' : '__none__')}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    if (v === '__none__') {
                      setClientId(null);
                      setClientName('');
                      setClientPhone('');
                      setClientEmail('');
                      setClientAddress('');
                      setClientGstin('');
                      return;
                    }
                    if (v === '__manual__') {
                      setClientId(null);
                      return;
                    }
                    const p = parties.find((x) => x.id === v);
                    if (!p) return;
                    setClientId(p.id);
                    setClientName(p.name);
                    setClientPhone(p.phone ?? '');
                    setClientEmail(p.email ?? '');
                    setClientAddress(p.address ?? '');
                    setClientGstin(p.panNumber ?? '');
                  }}
                >
                  <option value="__none__">— Pick a client —</option>
                  {parties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.phone ? ` · ${p.phone}` : ''}
                    </option>
                  ))}
                  <option value="__manual__">+ Type details manually</option>
                </select>
              </div>
              <div>
                <label style={FIELD_LABEL}>Client name *</label>
                <input
                  style={INPUT}
                  value={clientName}
                  onChange={(e) => setClientName(e.currentTarget.value)}
                  placeholder="Mr. Ramesh Sharma"
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Client GSTIN</label>
                <input
                  style={INPUT}
                  value={clientGstin}
                  onChange={(e) =>
                    setClientGstin(e.currentTarget.value.toUpperCase())
                  }
                  placeholder="29AAAAA0000A1Z5"
                  maxLength={15}
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Email</label>
                <input
                  style={INPUT}
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.currentTarget.value)}
                  placeholder="name@example.com"
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Phone</label>
                <input
                  style={INPUT}
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.currentTarget.value)}
                  placeholder="+91 90000 00000"
                />
              </div>
              <div></div>
            </div>
            <div>
              <label style={FIELD_LABEL}>Client address</label>
              <textarea
                style={{ ...INPUT, minHeight: 70, resize: 'vertical' }}
                value={clientAddress}
                onChange={(e) => setClientAddress(e.currentTarget.value)}
                placeholder="Street, City, State - PIN"
              />
            </div>
          </Section>

          {/* §2 Quotation details */}
          <Section
            num={2}
            title="Quotation details"
            subtitle="Numbering, dates and reference info."
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 12,
              }}
            >
              <div>
                <label style={FIELD_LABEL}>Quote number *</label>
                <input
                  style={INPUT}
                  value={number}
                  onChange={(e) => setNumber(e.currentTarget.value)}
                  placeholder="QT-YYMM-NNN"
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Issue date</label>
                <input
                  style={INPUT}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.currentTarget.value || todayKey())}
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Valid until</label>
                <input
                  style={INPUT}
                  type="date"
                  value={validUntil}
                  onChange={(e) =>
                    setValidUntil(e.currentTarget.value || plusDaysKey(15))
                  }
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Reference / Project</label>
                <input
                  style={INPUT}
                  value={reference}
                  onChange={(e) => setReference(e.currentTarget.value)}
                  placeholder="PO / Project ref"
                />
              </div>
            </div>
          </Section>
        </div>
      ) : null}

      {/* ============ STEP 2 — Items ============ */}
      {step === 2 ? (
        <div
          className="card"
          style={{ padding: 0, overflow: 'hidden', marginBottom: 90 }}
        >
          {/* §3 Modules & line items */}
          <Section
            num={3}
            title="Modules & line items"
            subtitle="Group items into modules. Drag modules or items to reorder. Each module has its own subtotal."
            right={
              <button
                type="button"
                className="btn-primary"
                onClick={addSection}
                style={{ fontSize: 13, padding: '8px 14px' }}
              >
                <i className="ph-bold ph-plus" /> Add module
              </button>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {sections.map((s) => (
                <ModuleCard
                  key={s.id}
                  section={s}
                  orgId={orgId}
                  onRenameSection={(t) => updateSection(s.id, { title: t })}
                  onDeleteSection={() => deleteSection(s.id)}
                  onAppendItem={(base) => appendItem(s.id, base)}
                  onUpdateItem={(itemId, patch) =>
                    updateItem(s.id, itemId, patch)
                  }
                  onDeleteItem={(itemId) => deleteItem(s.id, itemId)}
                  onDragModuleStart={() => {
                    dragModuleRef.current = s.id;
                  }}
                  onDragModuleEnd={() => {
                    dragModuleRef.current = null;
                  }}
                  onDragModuleOver={() => reorderModule(s.id)}
                  onDragItemStart={(itemId) => {
                    dragItemRef.current = { secId: s.id, itemId };
                  }}
                  onDragItemEnd={() => {
                    dragItemRef.current = null;
                  }}
                  onDragItemOver={(itemId) => reorderItem(s.id, itemId)}
                />
              ))}
              {sections.length === 0 ? (
                <div
                  style={{
                    padding: 18,
                    textAlign: 'center',
                    color: 'var(--text-tertiary)',
                    border: '1px dashed var(--border)',
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  No modules yet. Click <b>Add module</b> above to start.
                </div>
              ) : null}
            </div>
          </Section>
        </div>
      ) : null}

      {/* ============ STEP 3 — Billing ============ */}
      {step === 3 ? (
        <div
          className="card"
          style={{ padding: 0, overflow: 'hidden', marginBottom: 90 }}
        >
          {/* §4 Tax & discount */}
          <Section
            num={4}
            title="Tax & discount"
            subtitle="Applied to the sum of all module subtotals."
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
              }}
            >
              <div>
                <label style={FIELD_LABEL}>Overall discount</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select
                    value={discountType}
                    onChange={(e) =>
                      setDiscountType(e.currentTarget.value as DiscountType)
                    }
                    style={{ ...INPUT, width: 80 }}
                  >
                    <option value="percent">%</option>
                    <option value="flat">₹</option>
                  </select>
                  <input
                    type="number"
                    style={INPUT}
                    min={0}
                    step="any"
                    value={discountValue || ''}
                    onChange={(e) =>
                      setDiscountValue(parseFloat(e.currentTarget.value) || 0)
                    }
                  />
                </div>
              </div>
              <div>
                <label style={FIELD_LABEL}>GST split</label>
                <select
                  style={INPUT}
                  value={gst.enabled ? gst.type : 'off'}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    if (v === 'off') setGst((g) => ({ ...g, enabled: false }));
                    else
                      setGst((g) => ({
                        ...g,
                        enabled: true,
                        type: v as 'intra' | 'inter',
                      }));
                  }}
                >
                  <option value="off">No GST</option>
                  <option value="intra">CGST + SGST (same state)</option>
                  <option value="inter">IGST (different state)</option>
                </select>
              </div>
              <div>
                <label style={FIELD_LABEL}>GST rate</label>
                <select
                  style={INPUT}
                  value={gst.rate}
                  onChange={(e) => {
                    // Capture value before setGst — same React
                    // event-deferral gotcha; e.currentTarget is
                    // null by the time the updater runs.
                    const r = parseInt(e.currentTarget.value, 10) as GstRate;
                    setGst((g) => ({ ...g, rate: r }));
                  }}
                  disabled={!gst.enabled}
                >
                  {GST_RATES.map((r) => (
                    <option key={r} value={r}>
                      {r}%
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Totals preview */}
            <div
              style={{
                marginTop: 14,
                background: '#F7F9FC',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <TotalRow
                label="Subtotal (all modules)"
                value={formatInr(totals.subtotal)}
              />
              <TotalRow
                label="Discount"
                value={`− ${formatInr(totals.discountAmount)}`}
              />
              {gst.enabled && gst.rate > 0 ? (
                gst.type === 'intra' ? (
                  <>
                    <TotalRow
                      label={`CGST (${gst.rate / 2}%)`}
                      value={formatInr(totals.cgstAmount)}
                    />
                    <TotalRow
                      label={`SGST (${gst.rate / 2}%)`}
                      value={formatInr(totals.sgstAmount)}
                    />
                  </>
                ) : (
                  <TotalRow
                    label={`IGST (${gst.rate}%)`}
                    value={formatInr(totals.igstAmount)}
                  />
                )
              ) : (
                <TotalRow label="GST" value={formatInr(0)} />
              )}
              {Math.abs(totals.roundOff) >= 0.01 ? (
                <TotalRow
                  label="Round-off"
                  value={`${totals.roundOff > 0 ? '+ ' : '− '}${formatInr(Math.abs(totals.roundOff))}`}
                />
              ) : null}
              <TotalRow
                label="Grand Total"
                value={formatInr(totals.total)}
                grand
              />
            </div>
          </Section>

          {/* §5 Payment terms */}
          <Section
            num={5}
            title="Payment terms"
            subtitle="Define milestones with a % breakup. Total must equal 100%."
            right={
              <button
                type="button"
                className="btn-secondary"
                onClick={addMilestone}
                style={{ fontSize: 12, padding: '6px 12px' }}
              >
                <i className="ph-bold ph-plus" /> Add milestone
              </button>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {milestonePercents.map((m, i) => (
                <div
                  key={m.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 120px 140px 32px',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    background: '#fff',
                  }}
                >
                  <input
                    style={INPUT}
                    value={m.label}
                    onChange={(e) =>
                      updateMilestone(i, { label: e.currentTarget.value })
                    }
                    placeholder="e.g. On signing"
                  />
                  <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      style={{ ...INPUT, paddingRight: 30, textAlign: 'right' }}
                      min={0}
                      max={100}
                      step="any"
                      value={m.percent || ''}
                      onChange={(e) =>
                        updateMilestone(i, {
                          percent: parseFloat(e.currentTarget.value) || 0,
                        })
                      }
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-tertiary)',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      %
                    </span>
                  </div>
                  <div
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 14,
                    }}
                  >
                    {formatInr(milestoneRows[i]?.amount ?? 0)}
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteMilestone(i)}
                    title="Remove milestone"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-tertiary)',
                      cursor: 'pointer',
                      padding: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className="ph ph-trash" />
                  </button>
                </div>
              ))}
              {milestonePercents.length === 0 ? (
                <div
                  style={{
                    padding: 18,
                    textAlign: 'center',
                    color: 'var(--text-tertiary)',
                    border: '1px dashed var(--border)',
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  No milestones yet. Click <b>Add milestone</b> to define a %
                  breakup.
                </div>
              ) : null}
            </div>
            <div
              style={{
                marginTop: 12,
                fontSize: 12.5,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                fontWeight: 600,
                color: milestoneSumOk ? 'var(--success)' : 'var(--warning)',
              }}
            >
              <span
                style={{
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: milestoneSumOk
                    ? 'var(--success-bg)'
                    : 'var(--warning-bg)',
                  color: milestoneSumOk ? 'var(--success)' : 'var(--warning)',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Sum: {milestoneSum.toFixed(2)}%
              </span>
              <span>
                {milestoneSumOk
                  ? 'Looks good ✓'
                  : `Should equal 100% (currently ${(milestoneSum - 100).toFixed(2)}% off)`}
              </span>
            </div>
          </Section>

          {/* §6 Notes & terms */}
          <Section
            num={6}
            title="Notes & terms"
            subtitle="Bank details, validity, scope, etc."
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
              }}
            >
              <div>
                <label style={FIELD_LABEL}>Notes</label>
                <textarea
                  style={{ ...INPUT, minHeight: 110, resize: 'vertical' }}
                  value={notes}
                  onChange={(e) => setNotes(e.currentTarget.value)}
                  placeholder="Thank you note, scope clarifications, etc."
                />
              </div>
              <div>
                <label style={FIELD_LABEL}>Terms & conditions</label>
                <textarea
                  style={{ ...INPUT, minHeight: 110, resize: 'vertical' }}
                  value={tncText}
                  onChange={(e) => setTncText(e.currentTarget.value)}
                  placeholder="One condition per line — they'll render as a numbered list on the printed quote."
                />
              </div>
            </div>
            <div
              style={{
                marginTop: 12,
                display: 'flex',
                gap: 18,
                fontSize: 12,
                color: 'var(--text-secondary)',
                flexWrap: 'wrap',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={showBank}
                  onChange={(e) => setShowBank(e.currentTarget.checked)}
                  style={{ accentColor: 'var(--brand)' }}
                />
                Show bank details on PDF
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={showSignature}
                  onChange={(e) => setShowSignature(e.currentTarget.checked)}
                  style={{ accentColor: 'var(--brand)' }}
                />
                Show signature block
              </label>
            </div>

            {isEdit && existing && existing.status !== 'draft' ? (
              <div style={{ marginTop: 14 }}>
                <label style={FIELD_LABEL}>Revision note</label>
                <input
                  style={INPUT}
                  value={revisionNote}
                  onChange={(e) => setRevisionNote(e.currentTarget.value)}
                  placeholder="What changed in this revision? (saved to the history snapshot)"
                  maxLength={160}
                />
              </div>
            ) : null}
          </Section>
        </div>
      ) : null}

      {/* ============ STEP 4 — Review (inline preview) ============ */}
      {step === 4 ? (
        <>
          {/* "Viewing R{n}" banner — shown when the user clicks a
              past revision in the history card below. The form
              state isn't touched; only the preview iframe swaps. */}
          {viewingRevisionVersion != null ? (
            <div
              style={{
                padding: '10px 14px',
                background: '#FEF3C7',
                border: '1px solid #FDE68A',
                borderRadius: 8,
                marginBottom: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                fontSize: 13,
              }}
            >
              <span style={{ color: '#92400E' }}>
                <i
                  className="ph-bold ph-info"
                  style={{ marginRight: 6 }}
                />
                Viewing historical revision R{viewingRevisionVersion}. Your
                in-progress edits are unaffected.
              </span>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setViewingRevisionVersion(null)}
                style={{ fontSize: 12 }}
              >
                ← Back to current edits
              </button>
            </div>
          ) : null}

          <div
            className="card"
            style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}
          >
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--border)',
                background: '#FAFBFD',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <i
                className="ph-bold ph-eye"
                style={{ fontSize: 16, color: 'var(--brand)' }}
              />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                  }}
                >
                  Review &amp; send
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginTop: 2,
                  }}
                >
                  {clientName || 'Unnamed client'} · {sections.length} module
                  {sections.length === 1 ? '' : 's'} · {itemCount} item
                  {itemCount === 1 ? '' : 's'} · Grand total{' '}
                  <b style={{ color: 'var(--text-primary)' }}>
                    {formatInr(totals.total)}
                  </b>
                </div>
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePrintFromBuilder}
                style={{ fontSize: 12, padding: '6px 12px' }}
              >
                <i className="ph ph-printer" /> Print / PDF
              </button>
            </div>
            <PreviewIframe
              quotation={
                viewingRevisionVersion != null
                  ? (revisions.find(
                      (r) => r.version === viewingRevisionVersion,
                    )?.snapshot ?? buildSyntheticQuotation())
                  : buildSyntheticQuotation()
              }
            />
          </div>

          {/* ── Version history card ─────────────────────────
              Only meaningful when editing — a fresh draft has no
              history yet. Shows the live "Current edits" row at
              the top (highlighted when not viewing a snapshot)
              plus every immutable revision below. */}
          {isEdit ? (
            <div
              className="card"
              style={{ padding: 0, overflow: 'hidden', marginBottom: 90 }}
            >
              <div
                className="card-header"
                style={{
                  background: '#F9FAFB',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div
                  className="card-title"
                  style={{
                    fontSize: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <span>VERSION HISTORY</span>
                  <span
                    style={{ fontSize: 10, color: 'var(--text-tertiary)' }}
                  >
                    {revisions.length + 1} version
                    {revisions.length === 0 ? '' : 's'}
                  </span>
                </div>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {/* In-progress edits row */}
                <RevisionRow
                  versionLabel={
                    existing && existing.version > 0
                      ? `R${existing.version} · Current edits`
                      : 'Current edits'
                  }
                  note={
                    revisionNote.trim() ||
                    'Unsaved changes you are working on now'
                  }
                  date="Right now"
                  active={viewingRevisionVersion == null}
                  onClick={() => setViewingRevisionVersion(null)}
                  isCurrent
                />
                {/* Every immutable snapshot */}
                {revisions.map((r) => (
                  <RevisionRow
                    key={r.id}
                    versionLabel={`R${r.version}`}
                    note={r.note || 'Saved revision'}
                    date={
                      r.createdAt
                        ? new Date(r.createdAt.toMillis()).toLocaleString(
                            'en-IN',
                            {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            },
                          )
                        : '—'
                    }
                    meta={r.createdByName}
                    active={viewingRevisionVersion === r.version}
                    onClick={() => setViewingRevisionVersion(r.version)}
                  />
                ))}
                {revisions.length === 0 ? (
                  <li
                    style={{
                      padding: '16px 18px',
                      fontSize: 12,
                      color: 'var(--text-tertiary)',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    No saved revisions yet. The first time you save a
                    change to this quote after it leaves draft, a
                    snapshot of the previous state will appear here.
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}

      {/* ============ STICKY ACTION BAR ============ */}
      <div
        style={{
          position: 'sticky',
          bottom: 16,
          marginTop: 16,
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: '14px 18px',
          boxShadow: '0 8px 24px rgba(15,23,42,.08)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 14,
            alignItems: 'center',
            color: 'var(--text-secondary)',
            fontSize: 13,
            flexWrap: 'wrap',
          }}
        >
          <span>
            <b style={{ color: 'var(--text-primary)' }}>{sections.length}</b>{' '}
            modules
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>·</span>
          <span>
            <b style={{ color: 'var(--text-primary)' }}>{itemCount}</b> items
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>·</span>
          <span>
            Total{' '}
            <b
              style={{
                color: 'var(--text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatInr(totals.total)}
            </b>
          </span>
          {/* Margin block — only visible when at least one item
              has costPrice tracked. Internal-only signal so the
              studio always sees their margin while building the
              quote. Hidden when no cost data exists (most studios
              who don't care about margin shouldn't see noise). */}
          {marginTotals.trackedCount > 0 ? (
            <>
              <span
                style={{
                  marginLeft: 6,
                  paddingLeft: 14,
                  borderLeft: '1px solid var(--border)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <span title="Internal cost — never shown on the client PDF">
                  Cost{' '}
                  <b
                    style={{
                      color: 'var(--text-primary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatInr(marginTotals.cost)}
                  </b>
                </span>
                <span style={{ color: 'var(--text-tertiary)' }}>·</span>
                <span title="Markup = Sale − Cost (across tracked items)">
                  Markup{' '}
                  <b
                    style={{
                      color:
                        marginTotals.markup >= 0
                          ? 'var(--success)'
                          : 'var(--critical)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatInr(marginTotals.markup)}
                  </b>
                  {marginTotals.cost > 0 ? (
                    <span
                      style={{
                        color: 'var(--text-tertiary)',
                        marginLeft: 4,
                        fontSize: 12,
                      }}
                    >
                      (
                      {Math.round(
                        (marginTotals.markup / marginTotals.cost) * 100,
                      )}
                      %)
                    </span>
                  ) : null}
                </span>
              </span>
            </>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Save draft is always available — works on any step. */}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => handleSave('draft')}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save draft'}
          </button>

          {/* Back button — hidden on step 1. */}
          {step > 1 ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={goPrev}
              disabled={busy}
            >
              <i className="ph ph-arrow-left" /> Back
            </button>
          ) : null}

          {/* Next OR Create & send. Last step also exposes Print. */}
          {step < 4 ? (
            <button
              type="button"
              className="btn-primary"
              onClick={goNext}
              disabled={busy}
            >
              Next{' '}
              <i
                className="ph ph-arrow-right"
                style={{ marginLeft: 4 }}
              />
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePrintFromBuilder}
                disabled={busy}
              >
                <i className="ph ph-printer" /> Print
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => handleSave('sent')}
                disabled={busy}
                style={{
                  background: '#15803D',
                  borderColor: '#15803D',
                }}
              >
                {busy
                  ? 'Saving…'
                  : isEdit
                    ? 'Save & mark sent'
                    : 'Create & mark sent'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Subcomponents
// ──────────────────────────────────────────────────────────────────

function Section({
  num,
  title,
  subtitle,
  right,
  children,
}: {
  num: number;
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: '22px 24px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'var(--brand-bg)',
              color: 'var(--brand)',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 800,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {num}
          </div>
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}
            >
              {title}
            </h3>
            {subtitle ? (
              <p
                style={{
                  margin: '2px 0 0',
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function CompanyBanner({
  org,
}: {
  org: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstin?: string;
    logoUrl?: string;
  };
}) {
  const name = org.name?.trim() || 'Your Studio';
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();

  // Stack every available field as its own row with an icon —
  // gives the issuer block letterhead-like clarity instead of
  // cramming everything into a single comma-separated line. Empty
  // fields render a soft "Add in Profile" prompt that nudges the
  // user to complete their studio details without blocking the
  // quote.
  const rows: { icon: string; label: string; value?: string; missing?: string }[] = [
    {
      icon: 'ph-map-pin',
      label: 'Address',
      value: org.address?.trim() || undefined,
      missing: 'Add in Profile',
    },
    {
      icon: 'ph-identification-card',
      label: 'GSTIN',
      value: org.gstin?.trim() || undefined,
      missing: 'Add in Profile',
    },
    {
      icon: 'ph-envelope-simple',
      label: 'Email',
      value: org.email?.trim() || undefined,
      missing: 'Add in Profile',
    },
    {
      icon: 'ph-phone',
      label: 'Phone',
      value: org.phone?.trim() || undefined,
      missing: 'Add in Profile',
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        alignItems: 'flex-start',
        padding: '22px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'linear-gradient(135deg, #FAFBFF, #F4F6FB)',
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 14,
          background: org.logoUrl
            ? `center / cover no-repeat url(${org.logoUrl})`
            : 'linear-gradient(135deg, var(--brand), #B91C1C)',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 800,
          fontSize: 26,
          flexShrink: 0,
          boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        }}
      >
        {org.logoUrl ? '' : initials || '·'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: 'var(--text-primary)',
              lineHeight: 1.2,
            }}
          >
            {name}
          </div>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 999,
              background: '#DCFCE7',
              color: '#15803D',
              letterSpacing: 0.5,
            }}
          >
            ISSUER
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '6px 24px',
            fontSize: 12.5,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
          }}
        >
          {rows.map((r) => (
            <div
              key={r.label}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                minWidth: 0,
              }}
            >
              <i
                className={`ph ${r.icon}`}
                style={{
                  fontSize: 14,
                  color: 'var(--text-tertiary)',
                  marginTop: 2,
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  {r.label}
                </div>
                <div
                  style={{
                    color: r.value
                      ? 'var(--text-primary)'
                      : 'var(--text-tertiary)',
                    fontStyle: r.value ? 'normal' : 'italic',
                    wordBreak: 'break-word',
                    fontWeight: r.value ? 500 : 400,
                  }}
                  title={r.value ?? r.missing}
                >
                  {r.value ?? r.missing}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ───── Module card ─────

function ModuleCard({
  section,
  orgId,
  onRenameSection,
  onDeleteSection,
  onAppendItem,
  onUpdateItem,
  onDeleteItem,
  onDragModuleStart,
  onDragModuleEnd,
  onDragModuleOver,
  onDragItemStart,
  onDragItemEnd,
  onDragItemOver,
}: {
  section: QuotationSection;
  orgId: string | null;
  onRenameSection: (t: string) => void;
  onDeleteSection: () => void;
  onAppendItem: (base?: Partial<QuotationItem>) => void;
  onUpdateItem: (itemId: string, patch: Partial<QuotationItem>) => void;
  onDeleteItem: (itemId: string) => void;
  onDragModuleStart: () => void;
  onDragModuleEnd: () => void;
  onDragModuleOver: () => void;
  onDragItemStart: (itemId: string) => void;
  onDragItemEnd: () => void;
  onDragItemOver: (itemId: string) => void;
}) {
  const subtotal = moduleSubtotal(section);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Add-row form draft. Includes the new dimension fields (W×H +
  // dimensionUnit), HSN, and the explicit Unit dropdown the user
  // asked to have back. `dimensionUnit` defaults to 'ft' because
  // most interior measurements in India are quoted in feet.
  const [draft, setDraft] = useState<{
    name: string;
    note: string;
    hsn: string;
    unit: string;
    qty: number;
    rate: number;
    disc: number;
    // Dimensions — count is new (N × L × W × H / N × W × H × D).
    count: number;
    width: number;
    height: number;
    depth: number;
    dimensionUnit: DimensionUnit;
    // Cost + markup — when filled, rate auto-derives.
    costPrice: number;
    markupType: DiscountType;
    markupValue: number;
    // Supply + Install split — when filled, rate auto-derives
    // as `supplyRate + installRate`.
    supplyRate: number;
    installRate: number;
  }>({
    name: '',
    note: '',
    hsn: '',
    unit: 'Nos',
    qty: 1,
    rate: 0,
    disc: 0,
    count: 1,
    width: 0,
    height: 0,
    depth: 0,
    dimensionUnit: 'ft',
    costPrice: 0,
    markupType: 'percent',
    markupValue: 0,
    supplyRate: 0,
    installRate: 0,
  });

  // The two enrichment sections start hidden so the form stays
  // light. Toggling on reveals the cost / supply inputs and
  // automatically wires the rate auto-fill.
  const [costMarkupOpen, setCostMarkupOpen] = useState(false);
  const [supplyInstallOpen, setSupplyInstallOpen] = useState(false);

  // Auto-derive quantity from count × W × H (× depth) when
  // dimensions are set AND the billing unit supports it
  // (Sq.ft, Rft, Cft, Cum). User can still override qty.
  function recomputeQtyFromDimensions(next: {
    count?: number;
    width?: number;
    height?: number;
    depth?: number;
    dimensionUnit?: DimensionUnit;
    unit?: string;
  }) {
    const c = next.count ?? draft.count;
    const w = next.width ?? draft.width;
    const h = next.height ?? draft.height;
    const dp = next.depth ?? draft.depth;
    const du = next.dimensionUnit ?? draft.dimensionUnit;
    const u = next.unit ?? draft.unit;
    const derived = deriveQuantityFromDimensions({
      count: c,
      width: w,
      height: h,
      depth: dp,
      dimensionUnit: du,
      unit: u,
    });
    if (derived != null) {
      setDraft((d) => ({ ...d, ...next, qty: derived }));
    } else {
      setDraft((d) => ({ ...d, ...next }));
    }
  }

  // Auto-derive rate from cost + markup. Called from the
  // costPrice / markupType / markupValue input handlers.
  function recomputeRateFromCostMarkup(next: {
    costPrice?: number;
    markupType?: DiscountType;
    markupValue?: number;
  }) {
    const cp = next.costPrice ?? draft.costPrice;
    const mt = next.markupType ?? draft.markupType;
    const mv = next.markupValue ?? draft.markupValue;
    const derived = deriveRateFromCostAndMarkup({
      costPrice: cp,
      markupType: mt,
      markupValue: mv,
      rate: draft.rate,
    });
    setDraft((d) => ({ ...d, ...next, rate: derived }));
  }

  // Auto-derive rate from supply + install split. Called from
  // those input handlers.
  function recomputeRateFromSupplyInstall(next: {
    supplyRate?: number;
    installRate?: number;
  }) {
    const sr = next.supplyRate ?? draft.supplyRate;
    const ir = next.installRate ?? draft.installRate;
    setDraft((d) => ({
      ...d,
      ...next,
      rate: (sr || 0) + (ir || 0),
    }));
  }

  function submitAddRow(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.name.trim()) return;
    const hasDimensions =
      draft.width > 0 ||
      draft.height > 0 ||
      draft.depth > 0 ||
      draft.count > 1;
    const hasCostMarkup = costMarkupOpen && draft.costPrice > 0;
    const hasSupplyInstall =
      supplyInstallOpen && (draft.supplyRate > 0 || draft.installRate > 0);
    onAppendItem({
      name: draft.name.trim(),
      description: draft.note.trim() || undefined,
      hsn: draft.hsn.trim() || undefined,
      unit: draft.unit,
      quantity: draft.qty || 1,
      rate: draft.rate || 0,
      discount: draft.disc || 0,
      // Dimensions — only persist when actually used.
      count: hasDimensions && draft.count > 1 ? draft.count : undefined,
      width: hasDimensions ? draft.width || undefined : undefined,
      height: hasDimensions ? draft.height || undefined : undefined,
      depth: hasDimensions ? draft.depth || undefined : undefined,
      dimensionUnit: hasDimensions ? draft.dimensionUnit : undefined,
      // Cost + markup — only persist when actually used.
      costPrice: hasCostMarkup ? draft.costPrice : undefined,
      markupType: hasCostMarkup ? draft.markupType : undefined,
      markupValue:
        hasCostMarkup && draft.markupValue > 0 ? draft.markupValue : undefined,
      // Supply + install — only persist when actually used.
      supplyRate:
        hasSupplyInstall && draft.supplyRate > 0
          ? draft.supplyRate
          : undefined,
      installRate:
        hasSupplyInstall && draft.installRate > 0
          ? draft.installRate
          : undefined,
    });
    setDraft({
      name: '',
      note: '',
      hsn: '',
      unit: draft.unit,
      qty: 1,
      rate: 0,
      disc: 0,
      count: 1,
      width: 0,
      height: 0,
      depth: 0,
      dimensionUnit: draft.dimensionUnit,
      costPrice: 0,
      markupType: draft.markupType,
      markupValue: 0,
      supplyRate: 0,
      installRate: 0,
    });
  }

  // When the user picks a service from the library modal, the
  // service's name + description + unit + rate + hsn all
  // pre-fill the add-row. The user can then tweak qty / disc /
  // dimensions before clicking Add.
  function handlePickService(s: ServiceLibraryItem) {
    setDraft((d) => ({
      ...d,
      name: s.name,
      note: s.description ?? '',
      hsn: s.hsn ?? '',
      unit: s.unit,
      rate: s.defaultRate ?? 0,
    }));
    setPickerOpen(false);
  }

  // Live amount preview so the user can see the line total
  // before clicking Add.
  const previewAmount =
    (draft.qty || 0) * (draft.rate || 0) * (1 - (draft.disc || 0) / 100);

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragModuleStart();
      }}
      onDragEnd={onDragModuleEnd}
      onDragOver={(e) => {
        e.preventDefault();
        onDragModuleOver();
      }}
      style={{
        border: '1px solid var(--border)',
        borderRadius: 14,
        background: '#fff',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          background: 'linear-gradient(180deg, #F8FAFF, #FFF)',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            cursor: 'grab',
            color: '#A3AFC2',
            display: 'grid',
            placeItems: 'center',
            height: 30,
            width: 30,
            borderRadius: 8,
          }}
          title="Drag module to reorder"
        >
          <i className="ph-bold ph-dots-six-vertical" />
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            color: 'var(--brand)',
            background: 'var(--brand-bg)',
            padding: '4px 10px',
            borderRadius: 999,
            textTransform: 'uppercase',
          }}
        >
          Module
        </span>
        {/* Editable module title — wrapped in a label that
            shows a pencil icon adjacent so the affordance is
            obvious (otherwise the title looks like a static
            heading). Subtle dashed underline reinforces "click
            to edit" without screaming "form field". */}
        <label
          style={{
            flex: 1,
            minWidth: 200,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 8px',
            borderRadius: 8,
            cursor: 'text',
            borderBottom: '1.5px dashed transparent',
            transition: 'border-color 0.15s, background 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderBottomColor = 'var(--brand)';
            e.currentTarget.style.background = '#FFF7F7';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderBottomColor = 'transparent';
            e.currentTarget.style.background = 'transparent';
          }}
          title="Click to rename this module"
        >
          <input
            value={section.title}
            onChange={(e) => onRenameSection(e.currentTarget.value)}
            placeholder="Module name (e.g. Design Phase)"
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
              padding: 0,
              cursor: 'text',
            }}
            onFocus={(e) => {
              const p = e.currentTarget.parentElement;
              if (p) {
                p.style.borderBottomColor = 'var(--brand)';
                p.style.background = '#FFF7F7';
              }
            }}
            onBlur={(e) => {
              const p = e.currentTarget.parentElement;
              if (p) {
                p.style.borderBottomColor = 'transparent';
                p.style.background = 'transparent';
              }
            }}
          />
          <i
            className="ph ph-pencil-simple"
            style={{
              fontSize: 13,
              color: 'var(--text-tertiary)',
              flexShrink: 0,
            }}
            aria-hidden
          />
        </label>
        <span
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            fontWeight: 600,
          }}
        >
          Subtotal:{' '}
          <b
            style={{
              color: 'var(--text-primary)',
              fontVariantNumeric: 'tabular-nums',
              marginLeft: 4,
            }}
          >
            {formatInr(subtotal)}
          </b>
        </span>
        <button
          type="button"
          onClick={onDeleteSection}
          title="Delete module"
          style={{
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '4px 8px',
            color: 'var(--critical)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <i className="ph ph-trash" />
        </button>
      </div>

      {/* Items */}
      <div style={{ padding: '10px 12px' }}>
        {section.items.length === 0 ? (
          <div
            style={{
              padding: 14,
              textAlign: 'center',
              color: 'var(--text-tertiary)',
              border: '1px dashed var(--border)',
              borderRadius: 10,
              fontSize: 13,
            }}
          >
            No items in this module yet. Add one below.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {section.items.map((it, i) => (
              <ItemRow
                key={it.id}
                index={i}
                item={it}
                onUpdate={(patch) => onUpdateItem(it.id, patch)}
                onDelete={() => onDeleteItem(it.id)}
                onDragStart={() => onDragItemStart(it.id)}
                onDragEnd={onDragItemEnd}
                onDragOver={() => onDragItemOver(it.id)}
              />
            ))}
          </div>
        )}

        {/* Add-row form — multi-row card layout. Real labels above
            every input, Unit dropdown restored, HSN visible,
            optional W × H dimensions that auto-fill Qty for
            Sq.ft + Rft, live Amount preview, single primary CTA. */}
        <form
          onSubmit={submitAddRow}
          style={{
            marginTop: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            padding: 16,
            background: '#FAFBFD',
            border: '1px dashed #D3DAE8',
            borderRadius: 12,
          }}
        >
          {/* Row 1 — Name + HSN + Unit */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 140px 130px',
              gap: 10,
            }}
          >
            <div>
              <label style={ADD_LABEL}>Item / service *</label>
              <input
                required
                style={{ ...ADD_INPUT }}
                value={draft.name}
                onChange={(e) => {
                  // Capture the value BEFORE the setDraft updater.
                  // React clears `e.currentTarget` after the
                  // synchronous handler returns, but the updater
                  // can run later (and twice in StrictMode), so
                  // reading `e.currentTarget.value` inside it
                  // crashes with "Cannot read properties of null".
                  // Same fix applies to every onChange below.
                  const v = e.currentTarget.value;
                  setDraft((d) => ({ ...d, name: v }));
                }}
                placeholder="e.g. Brand identity design"
              />
            </div>
            <div>
              <label style={ADD_LABEL}>HSN / SAC</label>
              <input
                style={{ ...ADD_INPUT }}
                value={draft.hsn}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setDraft((d) => ({ ...d, hsn: v }));
                }}
                placeholder="e.g. 998314"
                maxLength={20}
              />
            </div>
            <div>
              <label style={ADD_LABEL}>Unit *</label>
              <select
                style={ADD_INPUT}
                value={draft.unit}
                onChange={(e) => {
                  const u = e.currentTarget.value;
                  // Changing the unit may re-enable auto-derive
                  // (e.g. Nos → Sq.ft with dimensions already
                  // typed). Run the recompute helper.
                  recomputeQtyFromDimensions({ unit: u });
                }}
              >
                {QUOTATION_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2 — Optional dimensions: N × W × H × D
              For interior items: "3 wardrobes × 8 ft × 6 ft × 24 in"
              auto-derives Qty for area (Sq.ft) / linear (Rft) /
              volume (Cft / Cum) billing units. Depth only matters
              for Cft + Cum; for Sq.ft the form still saves depth
              if entered so the printed spec stays complete. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '80px 14px 110px 14px 110px 14px 110px 90px 1fr',
              gap: 6,
              alignItems: 'end',
            }}
          >
            <div>
              <label style={ADD_LABEL}>Count</label>
              <input
                type="number"
                style={{ ...ADD_INPUT, textAlign: 'right' }}
                min={1}
                step={1}
                value={draft.count || ''}
                onChange={(e) =>
                  recomputeQtyFromDimensions({
                    count: parseInt(e.currentTarget.value, 10) || 1,
                  })
                }
                placeholder="1"
              />
            </div>
            <DimSeparator>×</DimSeparator>
            <div>
              <label style={ADD_LABEL}>Width</label>
              <input
                type="number"
                style={{ ...ADD_INPUT, textAlign: 'right' }}
                min={0}
                step="any"
                value={draft.width || ''}
                onChange={(e) =>
                  recomputeQtyFromDimensions({
                    width: parseFloat(e.currentTarget.value) || 0,
                  })
                }
                placeholder="0"
              />
            </div>
            <DimSeparator>×</DimSeparator>
            <div>
              <label style={ADD_LABEL}>Height</label>
              <input
                type="number"
                style={{ ...ADD_INPUT, textAlign: 'right' }}
                min={0}
                step="any"
                value={draft.height || ''}
                onChange={(e) =>
                  recomputeQtyFromDimensions({
                    height: parseFloat(e.currentTarget.value) || 0,
                  })
                }
                placeholder="0"
              />
            </div>
            <DimSeparator>×</DimSeparator>
            <div>
              <label style={ADD_LABEL}>
                Depth
                <span
                  style={{
                    fontWeight: 500,
                    color: 'var(--text-tertiary)',
                    textTransform: 'none',
                    letterSpacing: 0,
                    marginLeft: 4,
                  }}
                >
                  (Cft/Cum)
                </span>
              </label>
              <input
                type="number"
                style={{ ...ADD_INPUT, textAlign: 'right' }}
                min={0}
                step="any"
                value={draft.depth || ''}
                onChange={(e) =>
                  recomputeQtyFromDimensions({
                    depth: parseFloat(e.currentTarget.value) || 0,
                  })
                }
                placeholder="0"
              />
            </div>
            <div>
              <label style={ADD_LABEL}>In</label>
              <select
                style={ADD_INPUT}
                value={draft.dimensionUnit}
                onChange={(e) =>
                  recomputeQtyFromDimensions({
                    dimensionUnit: e.currentTarget.value as DimensionUnit,
                  })
                }
              >
                {DIMENSION_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                paddingBottom: 8,
                fontSize: 11,
                color: 'var(--text-tertiary)',
                lineHeight: 1.4,
              }}
            >
              {['Sq.ft', 'Rft', 'Cft', 'Cum'].includes(draft.unit) &&
              draft.width > 0
                ? `Auto-fills Qty (${draft.unit}). Manual edit still wins.`
                : 'Optional. For Nos / Kg / Hrs etc., type Qty directly.'}
            </div>
          </div>

          {/* Row 2b — Toggles for the optional cost+markup and
              supply+install splits. Both hidden by default to
              keep the form light for studios that don't price
              by margin. */}
          <div
            style={{
              display: 'flex',
              gap: 14,
              fontSize: 12,
              color: 'var(--text-secondary)',
              alignItems: 'center',
            }}
          >
            <label
              style={{
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={costMarkupOpen}
                onChange={(e) => setCostMarkupOpen(e.currentTarget.checked)}
                style={{ accentColor: 'var(--brand)' }}
              />
              <i className="ph ph-chart-line-up" /> Track cost + markup
            </label>
            <label
              style={{
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={supplyInstallOpen}
                onChange={(e) =>
                  setSupplyInstallOpen(e.currentTarget.checked)
                }
                style={{ accentColor: 'var(--brand)' }}
              />
              <i className="ph ph-stack" /> Split into Supply + Install
            </label>
          </div>

          {/* Cost + Markup — drives the Rate auto-fill below. */}
          {costMarkupOpen ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 120px 90px 1fr',
                gap: 8,
                alignItems: 'end',
                padding: 10,
                background: '#FFF7ED',
                border: '1px solid #FED7AA',
                borderRadius: 8,
              }}
            >
              <div>
                <label style={ADD_LABEL}>Cost price (₹)</label>
                <input
                  type="number"
                  style={{ ...ADD_INPUT, textAlign: 'right' }}
                  min={0}
                  step="any"
                  value={draft.costPrice || ''}
                  onChange={(e) =>
                    recomputeRateFromCostMarkup({
                      costPrice: parseFloat(e.currentTarget.value) || 0,
                    })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <label style={ADD_LABEL}>Markup</label>
                <input
                  type="number"
                  style={{ ...ADD_INPUT, textAlign: 'right' }}
                  min={0}
                  step="any"
                  value={draft.markupValue || ''}
                  onChange={(e) =>
                    recomputeRateFromCostMarkup({
                      markupValue: parseFloat(e.currentTarget.value) || 0,
                    })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <label style={ADD_LABEL}>Type</label>
                <select
                  style={ADD_INPUT}
                  value={draft.markupType}
                  onChange={(e) =>
                    recomputeRateFromCostMarkup({
                      markupType: e.currentTarget.value as DiscountType,
                    })
                  }
                >
                  <option value="percent">%</option>
                  <option value="flat">₹</option>
                </select>
              </div>
              <div
                style={{
                  paddingBottom: 8,
                  fontSize: 11,
                  color: '#9A3412',
                  lineHeight: 1.4,
                }}
              >
                <i className="ph-bold ph-info" style={{ marginRight: 4 }} />
                Auto-fills Rate. Internal — never shown on the
                client PDF. Margin shows in the footer below.
              </div>
            </div>
          ) : null}

          {/* Supply + Install — drives the Rate auto-fill below. */}
          {supplyInstallOpen ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 140px 1fr',
                gap: 8,
                alignItems: 'end',
                padding: 10,
                background: '#EFF6FF',
                border: '1px solid #BFDBFE',
                borderRadius: 8,
              }}
            >
              <div>
                <label style={ADD_LABEL}>Supply rate (₹)</label>
                <input
                  type="number"
                  style={{ ...ADD_INPUT, textAlign: 'right' }}
                  min={0}
                  step="any"
                  value={draft.supplyRate || ''}
                  onChange={(e) =>
                    recomputeRateFromSupplyInstall({
                      supplyRate: parseFloat(e.currentTarget.value) || 0,
                    })
                  }
                  placeholder="0"
                />
              </div>
              <div>
                <label style={ADD_LABEL}>Install rate (₹)</label>
                <input
                  type="number"
                  style={{ ...ADD_INPUT, textAlign: 'right' }}
                  min={0}
                  step="any"
                  value={draft.installRate || ''}
                  onChange={(e) =>
                    recomputeRateFromSupplyInstall({
                      installRate: parseFloat(e.currentTarget.value) || 0,
                    })
                  }
                  placeholder="0"
                />
              </div>
              <div
                style={{
                  paddingBottom: 8,
                  fontSize: 11,
                  color: '#1E3A8A',
                  lineHeight: 1.4,
                }}
              >
                <i className="ph-bold ph-info" style={{ marginRight: 4 }} />
                Sum auto-fills Rate. Split shown on the printed
                quote so clients see material vs labour.
              </div>
            </div>
          ) : null}

          {/* Row 3 — Qty, Rate, Disc, Amount preview */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '120px 140px 100px 1fr',
              gap: 10,
              alignItems: 'end',
            }}
          >
            <div>
              <label style={ADD_LABEL}>Qty *</label>
              <input
                type="number"
                required
                style={{ ...ADD_INPUT, textAlign: 'right' }}
                min={0}
                step="any"
                value={draft.qty || ''}
                onChange={(e) => {
                  const v = parseFloat(e.currentTarget.value) || 0;
                  setDraft((d) => ({ ...d, qty: v }));
                }}
              />
            </div>
            <div>
              <label style={ADD_LABEL}>Rate (₹) *</label>
              <input
                type="number"
                required
                style={{ ...ADD_INPUT, textAlign: 'right' }}
                min={0}
                step="any"
                value={draft.rate || ''}
                onChange={(e) => {
                  const v = parseFloat(e.currentTarget.value) || 0;
                  setDraft((d) => ({ ...d, rate: v }));
                }}
              />
            </div>
            <div>
              <label style={ADD_LABEL}>Disc %</label>
              <input
                type="number"
                style={{ ...ADD_INPUT, textAlign: 'right' }}
                min={0}
                max={100}
                step="any"
                value={draft.disc || ''}
                onChange={(e) => {
                  const v = parseFloat(e.currentTarget.value) || 0;
                  setDraft((d) => ({ ...d, disc: v }));
                }}
              />
            </div>
            <div>
              <label style={ADD_LABEL}>Amount</label>
              <div
                style={{
                  ...ADD_INPUT,
                  background: '#fff',
                  fontWeight: 700,
                  fontSize: 14,
                  color:
                    previewAmount > 0
                      ? 'var(--text-primary)'
                      : 'var(--text-tertiary)',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {previewAmount > 0 ? formatInr(previewAmount) : '—'}
              </div>
            </div>
          </div>

          {/* Row 4 — Description (full width) */}
          <div>
            <label style={ADD_LABEL}>Description</label>
            <textarea
              rows={2}
              style={{
                ...ADD_INPUT,
                resize: 'vertical',
                minHeight: 48,
                lineHeight: 1.45,
                fontFamily: 'inherit',
              }}
              value={draft.note}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setDraft((d) => ({ ...d, note: value }));
                const ta = e.currentTarget;
                ta.style.height = 'auto';
                ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
              }}
              placeholder="Optional — scope, deliverables, brand, finish…"
            />
          </div>

          {/* Row 5 — Footer actions: Library picker (left) + Add (right) */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              paddingTop: 4,
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPickerOpen(true)}
              style={{ fontSize: 12, padding: '8px 14px' }}
              title="Pick a service from your library to autofill this row"
            >
              <i className="ph ph-books" /> From Services Library
            </button>
            <div style={{ flex: 1 }} />
            <button
              type="submit"
              className="btn-primary"
              style={{ padding: '9px 22px' }}
              disabled={!draft.name.trim()}
            >
              <i className="ph-bold ph-plus" /> Add to module
            </button>
          </div>
        </form>

        {/* Module foot — just the subtotal display. The
            "From Services Library" button now lives inside
            the add-row card above, opening a centered modal
            instead of an in-line dropdown. */}
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <span
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              fontWeight: 600,
            }}
          >
            Module subtotal:{' '}
            <b
              style={{
                color: 'var(--text-primary)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatInr(subtotal)}
            </b>
          </span>
        </div>
      </div>

      {/* Services library picker — centered modal. Opens from
          the "From Services Library" button inside the add-row
          card. On pick, autofills name + description + unit +
          rate + hsn into the draft (user can then tweak qty /
          dimensions / disc before tapping Add). */}
      <ServicePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        orgId={orgId}
        onPick={handlePickService}
      />
    </div>
  );
}

// ───── Add-row form style tokens ─────

// Centered "×" separator for the dimensions row — replaces a
// bare text node so the column width stays predictable.
function DimSeparator({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        textAlign: 'center',
        fontSize: 14,
        color: 'var(--text-tertiary)',
        paddingBottom: 9,
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  );
}

const ADD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  marginBottom: 5,
};

const ADD_INPUT: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  outline: 'none',
};

// ───── Item row ─────

function ItemRow({
  index,
  item,
  onUpdate,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
}: {
  index: number;
  item: QuotationItem;
  onUpdate: (patch: Partial<QuotationItem>) => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: () => void;
}) {
  const [open, setOpen] = useState(false);
  const amt = itemAmount(item);
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver();
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr 70px 80px 110px 80px 110px 28px',
        gap: 10,
        alignItems: 'center',
        padding: '10px 10px',
        border: '1px solid var(--border)',
        borderRadius: 10,
        background: open ? '#FAFBFD' : '#fff',
        transition: 'background 0.12s',
      }}
    >
      <span
        style={{
          cursor: 'grab',
          color: '#B0B9C9',
          display: 'grid',
          placeItems: 'center',
          height: 24,
          width: 24,
          borderRadius: 6,
        }}
        title="Drag to reorder"
      >
        <i className="ph-bold ph-dots-six-vertical" />
      </span>
      <div style={{ minWidth: 0 }}>
        {/* Item name — dashed underline + hover + focus borders
            make it visually obvious that the title is editable
            (previously it looked like a static heading). */}
        <input
          className="qt-inline-edit"
          style={{
            width: '100%',
            border: '1px solid transparent',
            borderBottom: '1px dashed #CBD5E1',
            background: 'transparent',
            outline: 'none',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
            padding: '4px 6px',
            borderRadius: 4,
            transition: 'border-color 0.12s, background 0.12s',
          }}
          value={item.name}
          onChange={(e) => onUpdate({ name: e.currentTarget.value })}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--brand)';
            e.currentTarget.style.background = '#FFF7F7';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'transparent';
            e.currentTarget.style.borderBottomColor = '#CBD5E1';
            e.currentTarget.style.background = 'transparent';
          }}
          onMouseEnter={(e) => {
            if (document.activeElement !== e.currentTarget) {
              e.currentTarget.style.background = '#F8FAFC';
            }
          }}
          onMouseLeave={(e) => {
            if (document.activeElement !== e.currentTarget) {
              e.currentTarget.style.background = 'transparent';
            }
          }}
          placeholder={`Item ${index + 1}`}
          title="Click to edit item name"
        />
        {open ? (
          // Multi-line description — grows with content (capped at
          // 120px) so long specs / brand notes are fully visible
          // instead of getting truncated.
          <textarea
            rows={1}
            style={{
              width: '100%',
              border: '1px solid var(--brand)',
              background: '#FFF7F7',
              outline: 'none',
              fontSize: 12,
              color: 'var(--text-secondary)',
              fontFamily: 'inherit',
              padding: '6px 8px',
              marginTop: 4,
              borderRadius: 6,
              resize: 'vertical',
              minHeight: 28,
              lineHeight: 1.45,
            }}
            value={item.description ?? ''}
            onChange={(e) => {
              const value = e.currentTarget.value;
              onUpdate({ description: value });
              const ta = e.currentTarget;
              ta.style.height = 'auto';
              ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
            }}
            onBlur={(e) => {
              // Collapse back to ellipsis view if empty + unfocused.
              if (!e.currentTarget.value.trim()) setOpen(false);
            }}
            placeholder="Description / spec / brand / dimensions…"
            autoFocus
          />
        ) : item.description ? (
          <div
            onClick={() => setOpen(true)}
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              marginTop: 2,
              cursor: 'text',
              padding: '2px 6px',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = '#F8FAFC')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = 'transparent')
            }
            title="Click to edit description"
          >
            <i
              className="ph ph-pencil-simple"
              style={{
                fontSize: 10,
                color: 'var(--text-tertiary)',
                marginRight: 4,
              }}
            />
            {item.description}
          </div>
        ) : (
          <div
            onClick={() => setOpen(true)}
            style={{
              fontSize: 11,
              color: 'var(--brand)',
              marginTop: 2,
              cursor: 'text',
              padding: '2px 6px',
              fontWeight: 600,
            }}
          >
            <i className="ph ph-plus" /> Add description
          </div>
        )}
      </div>
      <input
        type="number"
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: 14,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          padding: '4px 6px',
          fontFamily: 'inherit',
        }}
        min={0}
        step="any"
        value={item.quantity || ''}
        onChange={(e) =>
          onUpdate({ quantity: parseFloat(e.currentTarget.value) || 0 })
        }
      />
      <select
        value={item.unit}
        onChange={(e) => onUpdate({ unit: e.currentTarget.value })}
        style={{
          border: 'none',
          background: 'transparent',
          fontSize: 12,
          fontFamily: 'inherit',
          padding: '4px 6px',
          color: 'var(--text-secondary)',
        }}
      >
        {QUOTATION_UNITS.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
      <input
        type="number"
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: 14,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          padding: '4px 6px',
          fontFamily: 'inherit',
        }}
        min={0}
        step="any"
        value={item.rate || ''}
        onChange={(e) =>
          onUpdate({ rate: parseFloat(e.currentTarget.value) || 0 })
        }
      />
      <input
        type="number"
        style={{
          width: '100%',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: 13,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          padding: '4px 6px',
          color: (item.discount ?? 0) > 0 ? 'var(--critical)' : 'var(--text-tertiary)',
          fontFamily: 'inherit',
        }}
        min={0}
        max={100}
        step="any"
        value={item.discount || ''}
        onChange={(e) =>
          onUpdate({ discount: parseFloat(e.currentTarget.value) || 0 })
        }
        placeholder="0"
      />
      <div
        style={{
          textAlign: 'right',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          fontSize: 14,
          padding: '4px 6px',
        }}
      >
        {formatInr(amt)}
      </div>
      <button
        type="button"
        onClick={onDelete}
        title="Remove row"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#B0B9C9',
          cursor: 'pointer',
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 6,
        }}
      >
        <i className="ph ph-trash" />
      </button>
    </div>
  );
}

// ───── Totals row ─────

function TotalRow({
  label,
  value,
  grand = false,
}: {
  label: string;
  value: string;
  grand?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: grand ? 16 : 13,
        color: grand ? 'var(--text-primary)' : 'var(--text-secondary)',
        fontWeight: grand ? 800 : 500,
        marginTop: grand ? 6 : 0,
        paddingTop: grand ? 10 : 0,
        borderTop: grand ? '1px solid var(--border)' : 'none',
      }}
    >
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

// ───── Inline preview iframe ─────

function PreviewIframe({ quotation }: { quotation: Quotation }) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  useEffect(() => {
    const f = ref.current;
    if (!f) return;
    const doc = f.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(buildQuotationHtml(quotation));
    doc.close();
  }, [quotation]);
  return (
    <iframe
      ref={ref}
      title="Quotation preview"
      style={{
        width: '100%',
        height: '85vh',
        border: 'none',
        display: 'block',
        background: '#fff',
      }}
    />
  );
}

// ───── Revision history row ─────

function RevisionRow({
  versionLabel,
  note,
  date,
  meta,
  active,
  isCurrent = false,
  onClick,
}: {
  versionLabel: string;
  note: string;
  date: string;
  meta?: string;
  active: boolean;
  isCurrent?: boolean;
  onClick: () => void;
}) {
  return (
    <li
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        padding: '12px 18px',
        borderTop: '1px solid var(--border)',
        cursor: 'pointer',
        background: active ? 'var(--brand-bg)' : 'transparent',
        transition: 'background 0.12s',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: active ? 'var(--brand)' : 'var(--text-primary)',
          }}
        >
          {versionLabel}
          {isCurrent ? (
            <span
              style={{
                marginLeft: 8,
                fontSize: 10,
                padding: '1px 7px',
                borderRadius: 999,
                background: 'var(--success-bg)',
                color: 'var(--success)',
                fontWeight: 700,
                letterSpacing: 0.4,
              }}
            >
              EDITING
            </span>
          ) : null}
        </span>
        <span
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            fontFamily: 'Menlo, monospace',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {date}
        </span>
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          marginTop: 4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {note}
      </div>
      {meta ? (
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            marginTop: 2,
          }}
        >
          {meta}
        </div>
      ) : null}
    </li>
  );
}

// ───── Bank details builder ─────

function buildBankDetails(org: {
  bankAccountName?: string;
  bankName?: string;
  bankBranch?: string;
  bankAccountNumber?: string;
  bankIfsc?: string;
  bankUpiId?: string;
}):
  | {
      accountName?: string;
      bankName?: string;
      branch?: string;
      accountNumber?: string;
      ifsc?: string;
      upiId?: string;
    }
  | undefined {
  const out: Record<string, string> = {};
  if (org.bankAccountName?.trim()) out.accountName = org.bankAccountName.trim();
  if (org.bankName?.trim()) out.bankName = org.bankName.trim();
  if (org.bankBranch?.trim()) out.branch = org.bankBranch.trim();
  if (org.bankAccountNumber?.trim())
    out.accountNumber = org.bankAccountNumber.trim();
  if (org.bankIfsc?.trim()) out.ifsc = org.bankIfsc.trim();
  if (org.bankUpiId?.trim()) out.upiId = org.bankUpiId.trim();
  return Object.keys(out).length === 0 ? undefined : out;
}
